import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import debounce from 'foundation/js/debounce'

export default {
    name: 'DictionaryWord',
    props: {
        domId: {
            type: String,
            required: true
        },
        wordObject: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const { id, word, wordArticleUrl } = props.wordObject
        const store = useStore()

        let display = ref(false)
        const wordDefinition = computed(() =>
            store.getters['dictionary/getWordDefinitionById'](id)
        )

        const fetchDefinition = debounce(function () {
            store.dispatch('dictionary/fetch_word_definition', { id })
        }, 500)

        const setESChandler = () => {
            const handler = (event) => {
                if (event.key === 'Escape') {
                    closeDialog()
                    document.body.removeEventListener('keydown', handler)
                }
            }
            document.body.addEventListener('keydown', handler)
        }

        const highlighted = ref(null)
        const wordPopup = ref(null)
        const arrow = ref(null)
        const closeButton = ref(null)

        const clickOutsideHandler = function (e) {
            if (wordPopup.value.contains(e.target)) return false
            e.preventDefault()
            closeDialog()
        }

        const tapTrap = (e) => {
            e.preventDefault()
            e.stopPropagation()
            closeDialog()
            highlighted.value.focus()
        }

        const showDialog = () => {
            if (store.state.main.dictionary_visible) return
            store.commit('main/set_dictionary_visible', true)

            fetchDefinition()
            setESChandler()

            display.value = true

            // we need to make sure the DOM has catched up and the popup element is present before we start to measure positions
            // wordPopup and arrow might be empty if we dont apply this slight wait
            setTimeout(() => {
                // original positions before calculations

                let arrowPosition = 0
                const currentLeftPosition =
                    wordPopup.value.getBoundingClientRect().left
                const currentRightPosition =
                    wordPopup.value.getBoundingClientRect().right
                const currentBottomPosition =
                    wordPopup.value.getBoundingClientRect().bottom

                const popupWidth = wordPopup.value.offsetWidth
                const highlightedWidthCompensation =
                    highlighted.value.offsetWidth / 2 - 10
                let calculatedLeftPosition =
                    popupWidth / 2 - highlightedWidthCompensation

                const exceedsBottomEdge =
                    currentBottomPosition >
                    document.documentElement.clientHeight
                const exceedsLeftEdge =
                    calculatedLeftPosition > currentLeftPosition
                const exceedsRightEdge =
                    currentRightPosition - calculatedLeftPosition >
                    document.documentElement.offsetWidth

                if (exceedsBottomEdge) {
                    wordPopup.value.style.bottom =
                        highlighted.value.offsetHeight + 10 + 'px'
                    arrow.value.style.bottom = '-35px' // specific value matching the actual height of the rotated square being used (do not change)
                    arrow.value.style.top = 'auto' // a reset to hardcounter styling
                }

                if (exceedsLeftEdge) {
                    calculatedLeftPosition =
                        calculatedLeftPosition -
                        (calculatedLeftPosition - currentLeftPosition)
                    arrowPosition =
                        highlighted.value.offsetWidth / 2 -
                        10 +
                        highlighted.value.getBoundingClientRect().left
                } else if (exceedsRightEdge) {
                    const calculatedRightPosition =
                        currentRightPosition -
                        calculatedLeftPosition -
                        document.documentElement.offsetWidth
                    calculatedLeftPosition =
                        calculatedLeftPosition + calculatedRightPosition
                    arrowPosition = popupWidth / 2 + calculatedRightPosition
                }

                wordPopup.value.style.left = '-' + calculatedLeftPosition + 'px'

                if (arrowPosition) {
                    arrow.value.style.left = arrowPosition + 'px'
                }

                document.body.addEventListener('click', clickOutsideHandler)

                closeButton.value.focus()
            }, 10)
        }

        onMounted(() => {
            /*
                We emit a 'dictionaryWordMounted' event when a dictionary word highlight is mounted,
                to allow other components to react to the mounting of a dictionary word highlight.
                E.g. the FactBox component listens to this event. It uses the event to control the timing of the word highlight. 
                Specifically, the highlight does not occur when the FactBox is closed. Instead, it only happens when the FactBox is opened.
                If there are other components that need to handle dictionary word highlights differently, they can listen to this event and react accordingly.
            */
            const dictionaryWordDom = document.querySelector(props.domId)
            const higlightDom = dictionaryWordDom.querySelector('span')

            const dictionaryWordMountedEvent = new CustomEvent(
                'dictionaryWordMounted',
                {
                    bubbles: true,
                    detail: higlightDom.innerText
                }
            )
            dictionaryWordDom.dispatchEvent(dictionaryWordMountedEvent)
        })

        const closeDialog = () => {
            document.body.removeEventListener('click', clickOutsideHandler)
            store.commit('main/set_dictionary_visible', false)
            display.value = false
        }

        return {
            arrow,
            closeDialog,
            closeButton,
            display,
            fetchDefinition,
            highlighted,
            id,
            showDialog,
            tapTrap,
            word,
            wordDefinition,
            wordArticleUrl,
            wordPopup
        }
    }
}
